import { hasAppliedToVacancy } from "../lib/applicationStatus.mjs";
import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { jobSalary } from "@/lib/jobSalary";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, SPONSORSHIP_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  return <VacancyForm key={JSON.stringify([id, user?.id, user?.tenant_id])} />;
}

function VacancyForm() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [answers, setAnswers] = useState({});
  const [assistantMessage, setAssistantMessage] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const applicationPending = useRef(false);
  const draftPending = useRef(false);
  const savePending = useRef(false);
  const [preparingDraft, setPreparingDraft] = useState(false);
  const [savingVacancy, setSavingVacancy] = useState(false);

  const [loadError, setLoadError] = useState("");
  const load = useCallback(async () => {
    setLoadError(""); setData(null); setAlreadyApplied(false);
    try {
      const jobResponse = await api.get(`/jobs/${id}`);
      if (user) {
        setAlreadyApplied(await hasAppliedToVacancy(api, id));
      }
      setData(jobResponse.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setLoadError(typeof detail === "string" ? detail : "Unable to load this vacancy or check your application status. Please try again.");
    }
  }, [id, user]);
  useEffect(() => { load(); }, [load]);

  const prepareDraft = async () => {
    if (draftPending.current) return;
    draftPending.current = true;
    setPreparingDraft(true);
    setAssistantMessage("");
    try {
      await api.post("/ai-applications/prepare", { job_id: id, answers });
      setAssistantMessage("Draft prepared. Open your AI application workspace to review it.");
    } catch (e) {
      setAssistantMessage(applicationErrorMessage(e, "Unable to prepare a draft. Please try again."));
    } finally {
      draftPending.current = false;
      setPreparingDraft(false);
    }
  };

  const saveJob = async () => {
    if (savePending.current) return;
    savePending.current = true;
    setSavingVacancy(true);
    setSavedMessage("");
    try {
      await api.post("/jobs/" + id + "/save");
      setSavedMessage("Vacancy saved. View it from your dashboard.");
    } catch (e) {
      setSavedMessage(applicationErrorMessage(e, "Unable to save this vacancy. Please try again."));
    } finally {
      savePending.current = false;
      setSavingVacancy(false);
    }
  };

  const apply = async () => {
    if (applicationPending.current) return;
    if (alreadyApplied) {
      setMsg("You have already applied for this vacancy. Duplicate applications are not allowed.");
      return;
    }
    applicationPending.current = true;
    setSubmitting(true);
    setMsg("");
    try {
      const { data } = await api.post("/applications", { job_id: id, answers, cover_letter: "" });
      setAlreadyApplied(true);
      setMsg(data.warning || "Your application has been submitted. Track it from your dashboard.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMsg(typeof detail === "string" ? detail : "Unable to submit application.");
    } finally {
      applicationPending.current = false;
      setSubmitting(false);
    }
  };

  if (loadError) return <div className="p-10"><p role="alert" className="text-sm text-red-700">{loadError}</p><button className="btn btn-primary mt-3" onClick={load}>Retry</button></div>;
  if (!data) return <p className="p-10 text-sm text-slate-500">Loading vacancy…</p>;
  const j = data.job;
  const label = SPONSORSHIP_LABELS[j.sponsorship_assessment?.label] || SPONSORSHIP_LABELS.not_established;

  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="job-detail-page">
      <h1 className="text-3xl font-semibold">{j.title}</h1>
      <p className="mt-2 text-slate-600">{j.company_name} · {j.town} · {jobSalary(j)}</p>
      <span className={`pill mt-4 ${label.cls}`} data-testid="job-sponsorship-label">{label.text}</span>
      <div className="mt-4">{user && <button data-testid="save-job-button" onClick={saveJob} disabled={savingVacancy} className="btn btn-ghost">{savingVacancy ? "Saving vacancy…" : "Save vacancy"}</button>} {savedMessage && <span className="ml-3 text-sm text-slate-600">{savedMessage}</span>}</div>

      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <section className="card p-6">
            <h2 className="text-lg font-semibold">Vacancy details</h2>
            <p className="mt-3 text-sm text-slate-700">{j.description}</p>
            <ul className="mt-4 text-sm text-slate-600 space-y-1">
              <li>Occupation code: {j.occupation_code || "Not supplied"}</li>
              <li>Experience requested: {j.experience_years === null || j.experience_years === undefined || j.experience_years === "" ? "Not supplied" : `${j.experience_years} ${Number(j.experience_years) === 1 ? "year" : "years"}`}</li>
              <li>Skills: {(j.skills || []).join(", ") || "Not supplied"}</li>
              <li>Professional registration: {j.professional_registration || "Not supplied"}</li>
              <li>Suitable for applicants outside the UK: {j.suitable_outside_uk ? "Yes" : "Not stated"}</li>
            </ul>
          </section>

          <section className="card p-6" data-testid="sponsorship-evidence">
            <h2 className="text-lg font-semibold">Sponsorship evidence</h2>
            <ul className="mt-3 text-sm text-slate-700 list-disc pl-5 space-y-1">
              {(j.sponsorship_assessment?.evidence || []).map((e, i) => <li key={i}>{e}</li>)}
              {(j.sponsorship_assessment?.evidence || []).length === 0 && <li>No supporting evidence recorded.</li>}
            </ul>
            <p className="mt-3 text-xs text-slate-500">
              Source: {j.sponsorship_assessment?.source} · Assessed {new Date(j.sponsorship_assessment?.assessed_at).toLocaleDateString("en-GB")}<br />
              {j.sponsorship_assessment?.disclaimer}
            </p>
          </section>

          {data.match && (
            <section className="card p-6" data-testid="match-explanation">
              <h2 className="text-lg font-semibold">Your match, dimension by dimension</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {Object.entries(data.match.dimensions).map(([k, v]) => (
                  <div key={k} className="border border-slate-200 rounded-lg p-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</p>
                    <p className="text-sm font-semibold mt-1">{v.score}%</p>
                    <p className="text-xs text-slate-600 mt-1">{v.explanation}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">{data.match.note}</p>
            </section>
          )}
        </div>

        <aside className="space-y-4">
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Apply</h2>
            {!user && <p className="mt-3 text-sm text-slate-600">Please <Link className="font-semibold text-[#1e3a8a]" to="/login">sign in</Link> to apply.</p>}
            {user && (
              <>
                {(j.screening_questions || []).map((q, i) => (
                  <label key={i} className="block text-sm mt-3">{q}
                    <input data-testid={`screening-answer-${i}`} className="field mt-1" value={answers[q] || ""}
                      onChange={(e) => setAnswers({ ...answers, [q]: e.target.value })} />
                  </label>
                ))}
                <button data-testid="prepare-application-button" onClick={prepareDraft} disabled={preparingDraft} className="btn btn-ghost mr-2">{preparingDraft ? "Preparing draft…" : "Prepare application draft"}</button>
                {assistantMessage && (
                  <div className="mt-3 text-sm text-slate-700" role="status">
                    <p>{assistantMessage}</p>
                    <Link to="/ai-applications" className="inline-block mt-2 font-semibold text-[#1e3a8a] underline">
                      Open AI application workspace
                    </Link>
                  </div>
                )}
          <button data-testid="apply-button" onClick={apply} disabled={alreadyApplied || submitting} aria-busy={submitting} className="btn btn-primary w-full justify-center mt-4 disabled:opacity-50">{alreadyApplied ? "Already applied" : submitting ? "Submitting…" : "Submit application"}</button>
                {alreadyApplied && <p className="mt-2 text-sm text-amber-700">You have already submitted an application for this vacancy.</p>}
              </>
            )}
            {msg && <p data-testid="apply-message" className="mt-3 text-sm text-slate-700">{msg}</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
